import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
  ChevronLeft,
  Edit,
  FileDown,
  FilePlus,
  FileText,
  Loader2,
  Power,
  PowerOff,
  RotateCcw,
} from "lucide-react";

import { CreditUnlockAlert } from "~/components/CreditUnlockAlert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { CardTitle } from "~/components/ui/card";
import { CreditProgress } from "~/components/ui/credit-progress";
import { toast } from "~/hooks/use-toast";
import { useClientActivationHistory } from "~/hooks/useClientActivationHistory";
import { useClientCreditUnlock } from "~/hooks/useClientCreditUnlock";
import { useSalesConditions } from "~/hooks/useSalesConditions";
import { supabase } from "~/integrations/supabase/client";
import { cn } from "~/lib/utils";
import { ClientActivationDialog } from "~/pages/clients/components/ClientActivationDialog";
import EditClientModal from "~/pages/clients/components/client-detail/EditClientModal";
import { useCreditUtilization } from "~/pages/clients/components/client-detail/hooks/useCreditUtilization";
import CreatePurchaseOrderDrawer from "~/pages/clients/components/client-detail/purchase-order/CreatePurchaseOrderDrawer";
import CreatePresupuestoDrawer from "~/pages/presupuestos/components/CreatePresupuestoDrawer";
import { useProfileStore } from "~/store/profileStore";
import { Client } from "~/types/client";
import { generateClientFichaPdf } from "~/utils/clientFichaPdf";
import { CreditUtilizationData } from "~/utils/creditUtilizationUtils";
import { formatCurrency, formatDate } from "~/utils/helpers";

interface ClientHeaderProps {
  client: Client;
}

const ClientStatusBadges: React.FC<{ client: Client }> = ({ client }) => {
  const isActive = client.is_active ?? client.isActive;
  // Only resolve the deactivation actor when the client is currently inactive.
  const { latestDeactivation } = useClientActivationHistory(
    !isActive ? String(client.id) : null
  );

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1.5">
        {isActive ? (
          <Badge
            variant="outline"
            className="border-green-200 bg-green-50 text-green-700"
          >
            Activo
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="border-gray-200 bg-gray-100 text-gray-600"
          >
            Inactivo
          </Badge>
        )}

        {client.risk_checked ? (
          <Badge
            variant="outline"
            className="border-green-200 bg-green-50 text-green-700"
          >
            Verificado
          </Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground">
            No verificado
          </Badge>
        )}

        {client.siniestro && (
          <Badge
            variant="outline"
            className="border-amber-200 bg-amber-50 text-amber-700"
          >
            Siniestro
          </Badge>
        )}

        {client.cobranza_judicial && (
          <Badge
            variant="outline"
            className="border-red-200 bg-red-50 text-red-700"
          >
            Cobranza judicial
          </Badge>
        )}
      </div>

      {!isActive && latestDeactivation && (
        <p className="text-xs text-muted-foreground">
          Desactivado por{" "}
          <span className="font-medium text-gray-700">
            {latestDeactivation.changed_by_name ?? "usuario desconocido"}
          </span>
          {latestDeactivation.changed_at
            ? ` · ${formatDate(latestDeactivation.changed_at)}`
            : ""}
        </p>
      )}
    </div>
  );
};

interface CreditSummaryCardProps {
  creditData: CreditUtilizationData;
  insuredAmount: number;
  className?: string;
}

const CreditSummaryCard: React.FC<CreditSummaryCardProps> = ({
  creditData,
  insuredAmount,
  className,
}) => {
  const utilizationPercentage =
    insuredAmount > 0
      ? (creditData.totalPendingAmount / insuredAmount) * 100
      : creditData.totalPendingAmount > 0
        ? 100
        : 0;

  const availableCredit = Math.max(
    0,
    insuredAmount - creditData.totalPendingAmount
  );

  const breakdownSources = [
    creditData.salesRequestUtilized,
    creditData.manualFacturasUtilized,
    creditData.uncollectedChecksAmount,
  ].filter((amount) => amount > 0);
  const showBreakdown = breakdownSources.length >= 2;

  return (
    <div className={cn("rounded-lg border p-4", className)}>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">Línea de Crédito</span>
        <span className="text-muted-foreground">
          {utilizationPercentage.toFixed(1)}%
        </span>
      </div>

      <CreditProgress value={utilizationPercentage} className="mt-2 h-2" />

      <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
        <div>
          <p className="text-xs text-muted-foreground">Utilizado</p>
          <p className="font-medium tabular-nums">
            {formatCurrency(creditData.utilized)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground">Disponible</p>
          <p className="font-medium tabular-nums">
            {formatCurrency(availableCredit)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Límite</p>
          <p className="font-medium tabular-nums">
            {formatCurrency(insuredAmount)}
          </p>
        </div>
      </div>

      {showBreakdown && (
        <div className="mt-3 space-y-1 border-t pt-3 text-xs text-muted-foreground">
          {creditData.salesRequestUtilized > 0 && (
            <div className="flex justify-between">
              <span>Notas de venta</span>
              <span className="tabular-nums">
                {formatCurrency(creditData.salesRequestUtilized)}
              </span>
            </div>
          )}
          {creditData.manualFacturasUtilized > 0 && (
            <div className="flex justify-between">
              <span>Facturas manuales</span>
              <span className="tabular-nums">
                {formatCurrency(creditData.manualFacturasUtilized)}
              </span>
            </div>
          )}
          {creditData.uncollectedChecksAmount > 0 && (
            <div className="flex justify-between">
              <span>Cheques pendientes</span>
              <span className="tabular-nums">
                {formatCurrency(creditData.uncollectedChecksAmount)}
              </span>
            </div>
          )}
        </div>
      )}

      {creditData.portfolioChecksAmount > 0 && (
        <p className="mt-3 border-t pt-3 text-xs text-muted-foreground">
          Cheques en cartera: {formatCurrency(creditData.portfolioChecksAmount)}{" "}
          — no afectan la línea de crédito
        </p>
      )}
    </div>
  );
};

const ClientHeader: React.FC<ClientHeaderProps> = ({ client }) => {
  const navigate = useNavigate();
  const { role } = useProfileStore();
  const isSeller = role === "seller";
  const isAdmin = role === "admin" || role === "superadmin";
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isPresupuestoDrawerOpen, setIsPresupuestoDrawerOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Activation dialog state
  const [isActivationDialogOpen, setIsActivationDialogOpen] = useState(false);

  // Client "ficha" Excel export state
  const [isExportingFicha, setIsExportingFicha] = useState(false);

  const { creditData, loading } = useCreditUtilization(String(client.id));
  const { getConditionById } = useSalesConditions();

  // Hook for fetching outstanding invoices - used for button disable logic
  const useOutstandingInvoices = (clientId: string | number) => {
    const [data, setData] = useState<any[]>([]);
    const [loadingInvoices, setLoadingInvoices] = useState(true);

    useEffect(() => {
      const fetchOutstandingInvoices = async () => {
        try {
          setLoadingInvoices(true);
          // Join with sales_request to get paid and payment_due_date fields
          // These columns exist on sales_request, not sales_request_documents
          const { data: invoices } = await supabase
            .from("sales_request_documents")
            .select(
              `
              *,
              sales_request:sales_request_id(
                id,
                paid,
                payment_due_date
              )
            `
            )
            .eq("client_id", clientId)
            .eq("document_type", "factura")
            .is("cancelled", false)
            .is("deleted_at", null)
            .not("bsale_number", "is", null);

          // Filter for overdue unpaid invoices in JavaScript
          // PostgREST doesn't support filtering on nested/joined columns
          const overdueInvoices = (invoices || []).filter((doc: any) => {
            const sr = doc.sales_request;
            return (
              sr &&
              sr.paid === false &&
              sr.payment_due_date &&
              new Date(sr.payment_due_date) < new Date()
            );
          });

          setData(overdueInvoices);
        } catch (error) {
          console.error("Error fetching outstanding invoices:", error);
        } finally {
          setLoadingInvoices(false);
        }
      };

      fetchOutstandingInvoices();
    }, [clientId]);

    return { data, loading: loadingInvoices };
  };

  const { data: outstandingInvoices } = useOutstandingInvoices(client.id);
  const hasOutstandingDebt =
    outstandingInvoices && outstandingInvoices.length > 0;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleClientUpdated = () => {
    // Dispatch custom event to refresh client data
    window.dispatchEvent(
      new CustomEvent("client-updated", {
        detail: { clientId: client.id },
      })
    );
  };

  const handleReactivateClient = async () => {
    const { error } = await supabase
      .from("clients")
      .update({ deleted_at: null })
      .eq("id", client.id);

    if (error) {
      toast({
        title: "Error",
        description: "No se pudo reactivar el cliente",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Cliente reactivado",
        description: "El cliente ha sido reactivado correctamente",
      });
      handleClientUpdated();
    }
  };

  // Handle activation toggle
  const handleActivationToggle = () => {
    setIsActivationDialogOpen(true);
  };

  const salesCondition = client.salesConditionId
    ? getConditionById(client.salesConditionId)
    : null;

  const handleExportFicha = async () => {
    try {
      setIsExportingFicha(true);
      const filename = await generateClientFichaPdf({
        client,
        salesConditionName: salesCondition?.name,
      });
      toast({
        title: "Ficha descargada",
        description: `Se descargó ${filename}.`,
      });
    } catch (error) {
      console.error("Error exporting client ficha:", error);
      toast({
        title: "Error",
        description: "No se pudo generar la ficha del cliente.",
        variant: "destructive",
      });
    } finally {
      setIsExportingFicha(false);
    }
  };

  const showCreditUtilization =
    !loading &&
    ((client.insuredAmount ?? 0) > 0 || creditData.totalPendingAmount > 0);

  const metaLine = [
    salesCondition?.name,
    client.trailingPaymentDays !== undefined
      ? `${client.trailingPaymentDays} días de pago`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  // Hook for checking/managing overdue debt unlock status
  const { isUnlocked: isOverdueDebtUnlocked } = useClientCreditUnlock(
    String(client.id),
    "overdue_debt"
  );

  const OutstandingDebtAlert = () => {
    // Don't show alert if no outstanding debt AND client is not unlocked
    if (!hasOutstandingDebt && !isOverdueDebtUnlocked) return null;

    // Show alert if there's outstanding debt OR if unlocked (to show the unlocked status)
    if (hasOutstandingDebt || isOverdueDebtUnlocked) {
      return (
        <CreditUnlockAlert
          clientId={String(client.id)}
          unlockType="overdue_debt"
          title="Atención: Cliente con deuda vencida"
          description={`Este cliente tiene ${outstandingInvoices?.length || 0} factura(s) vencida(s). Revise la situación antes de crear nuevas notas de venta.`}
        />
      );
    }

    return null;
  };

  // Mobile view for sellers
  if (isMobile && isSeller) {
    return (
      <>
        <div className="flex flex-col justify-between gap-y-4">
          <Button variant="outline" onClick={() => navigate("/clients")}>
            <ChevronLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>

          <div className="flex flex-col gap-2">
            <Button
              className="flex items-center gap-2"
              onClick={() => setIsDrawerOpen(true)}
              disabled={hasOutstandingDebt && !isOverdueDebtUnlocked}
            >
              <FilePlus className="h-4 w-4" />
              Crear Nota de Venta
            </Button>
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => setIsPresupuestoDrawerOpen(true)}
            >
              <FileText className="h-4 w-4" />
              Nueva Cotización
            </Button>
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={handleExportFicha}
              disabled={isExportingFicha}
            >
              {isExportingFicha ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4" />
              )}
              Exportar ficha
            </Button>
          </div>
        </div>

        {/* Outstanding Debt Alert - Mobile */}
        <OutstandingDebtAlert />

        <div className="space-y-3">
          <div>
            <CardTitle className="text-xl font-bold">
              {client.company}
            </CardTitle>
            <div className="mt-2">
              <ClientStatusBadges client={client} />
            </div>
            {metaLine && (
              <p className="mt-2 text-sm text-muted-foreground">{metaLine}</p>
            )}
          </div>

          {showCreditUtilization && (
            <CreditSummaryCard
              creditData={creditData}
              insuredAmount={client.insuredAmount || 0}
              className="w-full"
            />
          )}
        </div>

        <CreatePurchaseOrderDrawer
          client={client}
          open={isDrawerOpen}
          onOpenChange={setIsDrawerOpen}
        />

        <CreatePresupuestoDrawer
          client={client}
          open={isPresupuestoDrawerOpen}
          onOpenChange={setIsPresupuestoDrawerOpen}
        />
      </>
    );
  }

  // Desktop view for all users or non-seller mobile
  return (
    <>
      <div className="flex flex-col justify-between gap-y-4 lg:flex-row lg:items-center">
        <Button variant="outline" onClick={() => navigate("/clients")}>
          <ChevronLeft className="mr-2 h-4 w-4" />
          Volver a la lista de clientes
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button
            className="flex items-center gap-2"
            onClick={() => setIsDrawerOpen(true)}
            disabled={hasOutstandingDebt && !isOverdueDebtUnlocked}
          >
            <FilePlus className="h-4 w-4" />
            Crear Nota de Venta
          </Button>
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={() => setIsPresupuestoDrawerOpen(true)}
          >
            <FileText className="h-4 w-4" />
            Nueva Cotización
          </Button>
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={handleExportFicha}
            disabled={isExportingFicha}
          >
            {isExportingFicha ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="h-4 w-4" />
            )}
            Exportar ficha
          </Button>

          {/* Activation/Deactivation button - Sellers can deactivate, Admins can reactivate */}
          {((isSeller && (client.is_active ?? client.isActive)) ||
            (isAdmin && !(client.is_active ?? client.isActive))) &&
            !client.deleted_at && (
              <Button
                variant="outline"
                onClick={handleActivationToggle}
                className={`flex items-center gap-2 ${
                  (client.is_active ?? client.isActive)
                    ? "border-red-600 text-red-600 hover:bg-red-50"
                    : "border-green-600 text-green-600 hover:bg-green-50"
                }`}
              >
                {(client.is_active ?? client.isActive) ? (
                  <>
                    <PowerOff className="h-4 w-4" />
                    Desactivar Cliente
                  </>
                ) : (
                  <>
                    <Power className="h-4 w-4" />
                    Activar Cliente
                  </>
                )}
              </Button>
            )}

          {/* Reactivate button for deleted clients */}
          {isAdmin && client.deleted_at && (
            <Button
              variant="outline"
              className="flex items-center gap-2 border-green-600 text-green-600 hover:bg-green-50"
              onClick={handleReactivateClient}
            >
              <RotateCcw className="h-4 w-4" />
              Reactivar Cliente
            </Button>
          )}
        </div>
      </div>

      {/* Outstanding Debt Alert - Desktop */}
      <OutstandingDebtAlert />

      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-2xl font-bold">
              {client.company}
            </CardTitle>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              title="Editar Cliente"
            >
              <Edit className="h-5 w-5" />
            </button>
            <ClientStatusBadges client={client} />
          </div>

          {metaLine && (
            <p className="mt-1 text-sm text-muted-foreground">{metaLine}</p>
          )}
        </div>

        {showCreditUtilization && (
          <CreditSummaryCard
            creditData={creditData}
            insuredAmount={client.insuredAmount || 0}
            className="w-full shrink-0 md:w-[360px]"
          />
        )}
      </div>

      <CreatePurchaseOrderDrawer
        client={client}
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
      />

      <CreatePresupuestoDrawer
        client={client}
        open={isPresupuestoDrawerOpen}
        onOpenChange={setIsPresupuestoDrawerOpen}
      />

      <EditClientModal
        client={client}
        isOpen={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        onClientUpdated={handleClientUpdated}
      />

      {/* Client Activation Dialog */}
      <ClientActivationDialog
        client={client}
        isOpen={isActivationDialogOpen}
        onClose={() => setIsActivationDialogOpen(false)}
        onClientUpdated={handleClientUpdated}
      />
    </>
  );
};

export default ClientHeader;
